import {
  forwardRef,
  Suspense,
  lazy,
  startTransition,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ComponentType,
  type JSX,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { renderInWorker } from '../perf/pipelineClient';
import rehypeReact from 'rehype-react';
import { unified, type Processor } from 'unified';
import { Fragment, jsx, jsxs } from 'react/jsx-runtime';
import type { Root as HastRoot, Element as HastElement, Text as HastText } from 'hast';
import { Check, Copy, ExternalLink, Link as LinkIcon } from 'lucide-react';
import 'katex/dist/katex.min.css';
import { usePreviewTheme, useThemeName } from './usePreviewTheme';
import { shortcutLabel } from '../keymap/platform';
import { useContextMenu, useLongPress, type ContextMenuEntry } from '../context-menu';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { mark, measure } from '../perf/marks';
import { flattenBlocks, type FlattenedPreview } from '../perf/flattenBlocks';
import { blockRendererRegistry } from '../blocks/blockRendererRegistry';

/** Render-debounce window (spec §4.1: "Debounced render (~120 ms)"). */
const RENDER_DEBOUNCE_MS = 120;
/** How many off-screen blocks the virtualizer keeps mounted around the viewport. */
const VIRTUAL_OVERSCAN = 8;
/** Initial height estimate per block, in px — refined as items measure. */
const VIRTUAL_DEFAULT_HEIGHT = 80;

const MermaidBlock = lazy(() => import('./MermaidBlock'));
const ChartBlock = lazy(() => import('./ChartBlock'));

// Built-in embed renderers, registered in-process. The core pipeline rewrites
// these fenced languages to `div.mp-embed`; here we map each kind to its lazy
// React renderer through the shared registry.
blockRendererRegistry.register({
  embedKind: 'mermaid',
  render: (code, theme) => <MermaidBlock code={code} theme={theme as DiagramTheme} />,
});
blockRendererRegistry.register({
  embedKind: 'vega',
  render: (code, theme) => <ChartBlock code={code} kind="vega" theme={theme as DiagramTheme} />,
});
blockRendererRegistry.register({
  embedKind: 'vega-lite',
  render: (code, theme) => <ChartBlock code={code} kind="vega-lite" theme={theme as DiagramTheme} />,
});

export interface RenderedPreviewProps {
  text: string;
  /** Reports the scroll container (for split-view scroll sync). */
  scrollRef?: (el: HTMLElement | null) => void;
  /**
   * Reading mode: the rendered view shown standalone (non-split). Switches to a
   * comfortable measure / larger type. When false (split), the preview stays
   * visually matched to the Crepe WYSIWYG for 1:1 parity.
   */
  reading?: boolean;
  /**
   * Reading-only text zoom multiplier (1.0 = 100%). Applied as the
   * `--mp-reading-font-scale` custom property on the scroll root so the
   * stylesheet scales the body's font-size and all em-derived block
   * spacing in one step. Ignored when {@link reading} is false.
   */
  readingFontScale?: number;
  /**
   * Reading-only content width preset (`'narrow' | 'wide' | 'full'`).
   * Surfaced as `data-reading-width` on the scroll root and styled by
   * `theme/index.css`. Ignored when {@link reading} is false.
   */
  readingWidth?: 'narrow' | 'wide' | 'full';
  /**
   * Invoked when a hyperlink in the rendered content is clicked (default
   * navigation is prevented). The host resolves external vs. internal/anchor.
   * Also reused by the context menu's "Open link" so the same scheme-allow-list
   * guard applies (no unguarded openExternal).
   */
  onLink?: (href: string) => void;
  /** Copy arbitrary text to the clipboard (host wires `platform.writeClipboardText`). */
  onCopyText?: (text: string) => void;
  /**
   * Rewrite an `<img>` src to a URL the webview can actually fetch. On Tauri,
   * a relative `./image.svg` next to the doc resolves to the asset-protocol
   * URL for that absolute file. External / data / blob URLs should be
   * returned unchanged. Omit to leave srcs unchanged (web fallback).
   */
  onResolveImageSrc?: (src: string) => string;
  /**
   * Click handler for `<img>` elements in the rendered preview. The host
   * uses this to open the image in a full-screen lightbox (reuses the
   * standalone ImageViewer's zoom/pan controls). `src` is the resolved
   * URL passed through {@link onResolveImageSrc}.
   */
  onImageClick?: (src: string, alt: string) => void;
  /**
   * Click handler for `mp-embed` blocks (Mermaid / Vega) in the rendered
   * preview. The host opens the captured SVG/canvas snapshot in the same
   * full-screen lightbox the inline image click uses. `kind` is the embed
   * type (`'mermaid' | 'vega' | 'vega-lite'`).
   */
  onEmbedClick?: (src: string, kind: string) => void;
}

/**
 * Imperative API exposed via `forwardRef`. With JS virtualization the target
 * of a `#slug` link (or scroll-sync line) may not be mounted in the DOM yet;
 * these methods consult the in-memory block index, ask the virtualizer to
 * scroll to the owning block, and let React mount it.
 */
export interface RenderedPreviewHandle {
  /** Underlying scroll container, or `null` before mount. */
  scrollEl(): HTMLElement | null;
  /** Scroll the block containing heading `id=slug` to the viewport top.
   *  Returns `true` if `slug` resolved to a block. */
  scrollToSlug(slug: string): boolean;
  /** Scroll the block stamped with `data-line ≤ line` (greatest such) to the
   *  viewport top. Returns `true` if a matching block was found. */
  scrollToLine(line: number): boolean;
  /** `data-line` of the topmost visible block, or `null`. */
  topLine(): number | null;
  /** First heading id in the topmost visible block, or `null`. */
  topSlug(): string | null;
}

type DivProps = JSX.IntrinsicElements['div'] & { node?: HastElement };
type AnchorProps = JSX.IntrinsicElements['a'] & { node?: HastElement };
type ImgProps = JSX.IntrinsicElements['img'] & { node?: HastElement };
type PreProps = JSX.IntrinsicElements['pre'] & { node?: HastElement };
type TableProps = JSX.IntrinsicElements['table'] & { node?: HastElement };

/**
 * Shared hover-revealed "Copy" pill used by every block-level affordance
 * (fenced code, table, Mermaid / Vega embed). Each call site supplies a
 * `getSource()` thunk so the button can pull the right thing at click time
 * (rendered code text, sliced markdown, embed source). Returns `''`/`null`
 * to no-op the click silently — useful when the block's source is missing.
 */
interface BlockCopyButtonProps {
  getSource: () => string;
  onCopyText: (text: string) => void;
  label?: string;
  copiedLabel?: string;
  ariaLabel?: string;
}

function BlockCopyButton({
  getSource,
  onCopyText,
  label = 'Copy',
  copiedLabel = 'Copied',
  ariaLabel,
}: BlockCopyButtonProps): ReactElement {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);
  const onClick = useCallback(() => {
    const text = getSource();
    if (!text) return;
    onCopyText(text);
    setCopied(true);
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopied(false), 1200);
  }, [getSource, onCopyText]);
  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    },
    [],
  );
  return (
    <div className="mp-block-copy-tools" aria-hidden={false}>
      <button
        type="button"
        className="mp-block-copy"
        onClick={onClick}
        aria-label={ariaLabel ?? (copied ? copiedLabel : label)}
        data-copied={copied ? '' : undefined}
      >
        {copied ? (
          <Check size={14} strokeWidth={2.25} aria-hidden />
        ) : (
          <Copy size={14} strokeWidth={2} aria-hidden />
        )}
        <span>{copied ? copiedLabel : label}</span>
      </button>
    </div>
  );
}

/**
 * Read source-range data attributes (stamped by `rehypeSourceLines`) off a
 * hast element and return the matching slice of the source markdown.
 * Returns `''` when the offsets are missing or out of range.
 */
function sliceMarkdownForNode(node: HastElement | undefined, source: string): string {
  if (!node) return '';
  const start = Number(node.properties?.dataSourceStart);
  const end = Number(node.properties?.dataSourceEnd);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return '';
  if (start < 0 || end > source.length) return '';
  return source.slice(start, end);
}

/** Pull the raw source text out of an `mp-embed` hast node. */
function embedSource(node: HastElement | undefined): string {
  if (!node) return '';
  const text = node.children.find((c): c is HastText => c.type === 'text');
  return text?.value ?? '';
}

/**
 * Diagram theming passed to Mermaid / Vega so their text picks up the active
 * PROSE font (sans/serif per theme) and theme-appropriate colors (softer for
 * reader themes). Derived from the active typography theme + the resolved
 * light/dark mode.
 */
export interface DiagramTheme {
  dark: boolean;
  /** Resolved prose font-family string (Inter or Source Serif 4 + fallbacks). */
  fontFamily: string;
  /** Reader theme → softer / lower-contrast palette. */
  reader: boolean;
}

/**
 * Map `div.mp-embed` (rewritten from mermaid/vega fenced blocks by the core
 * pipeline's post-sanitize embed step) to a lazy client renderer; everything
 * else renders as a plain `<div>`. Errors inside a renderer are caught locally
 * (see Mermaid/Chart components) so a bad embed never crashes the preview.
 */
/**
 * Properties that drive the visible look of a Mermaid SVG node — we bake
 * the live computed values for these onto each clone element so the
 * standalone serialized SVG looks identical to the inline preview (which
 * relies on external CSS + `--mp-*` variables that don't follow the SVG
 * out of the document).
 */
const SVG_VISUAL_PROPS = [
  'fill',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'stroke-dasharray',
  'stroke-linecap',
  'stroke-linejoin',
  'color',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'text-anchor',
  'dominant-baseline',
] as const;

/**
 * Walk `orig` and `clone` in parallel, copying live computed styles from
 * the in-document `orig` element onto inline `style` of the matching
 * `clone` element. After this pass the clone carries everything it needs
 * to render the same way without the host document's stylesheet.
 */
function bakeSvgStyles(orig: Element, clone: Element): void {
  const computed = window.getComputedStyle(orig);
  let style = clone.getAttribute('style') ?? '';
  for (const prop of SVG_VISUAL_PROPS) {
    const v = computed.getPropertyValue(prop);
    if (v) style += `${prop}:${v};`;
  }
  clone.setAttribute('style', style);
  for (let i = 0; i < orig.children.length && i < clone.children.length; i++) {
    bakeSvgStyles(orig.children[i], clone.children[i]);
  }
}

/**
 * Capture the visible rendering of an embed (Mermaid SVG, Vega canvas) as a
 * lightbox-friendly URL. Returns `null` when the embed hasn't finished
 * rendering yet (the user clicked during the loading fallback).
 *
 * SVGs are deep-cloned with their computed visual properties baked into
 * inline `style` attributes so they survive being viewed standalone —
 * Mermaid relies on external `.mp-embed--mermaid` CSS (which references
 * `--mp-*` variables), and stripping that out leaves black, unstyled
 * shapes. The clone is wrapped in a `data:image/svg+xml` URL (CSP `data:`
 * is allowed for `img-src`). Canvases use `toDataURL('image/png')`.
 *
 * IMPORTANT: the search is scoped to the actual diagram host
 * (`.mp-embed__canvas`), NOT the outer click wrapper. The wrapper also
 * contains the BlockCopyButton's lucide `<svg>` icon — a naive
 * `wrapper.querySelector('svg')` would find that copy-icon first for
 * Vega charts rendered to a `<canvas>` (no chart SVG to shadow it),
 * yielding a tiny blank "icon" in the lightbox instead of the chart.
 * Mermaid happens to mask this bug because its chart SVG comes earlier
 * in DOM order than the copy button.
 */
export function embedToDataUrl(wrapper: HTMLElement): string | null {
  // Prefer the chart's actual host; fall back to the wrapper (e.g. older
  // markup without `.mp-embed__canvas`) but never reach into the copy
  // button's icon.
  const host =
    wrapper.querySelector<HTMLElement>('.mp-embed__canvas') ?? wrapper;
  const svg = host.querySelector('svg');
  if (svg) {
    const clone = svg.cloneNode(true) as SVGElement;
    try {
      bakeSvgStyles(svg, clone);
    } catch {
      /* If getComputedStyle fails for any reason, fall back to the raw
         serialized SVG — degraded but at least visible. */
    }
    const xml = new XMLSerializer().serializeToString(clone);
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
  }
  const canvas = host.querySelector('canvas');
  if (canvas) {
    try {
      return canvas.toDataURL('image/png');
    } catch {
      return null;
    }
  }
  return null;
}

function makeEmbedDiv(
  theme: DiagramTheme,
  onEmbedClick: ((src: string, kind: string) => void) | undefined,
  onCopyText: (text: string) => void,
): ComponentType<DivProps> {
  return function EmbedDiv({ node, children, ...rest }: DivProps): ReactElement {
    const embed = node?.properties?.['data-embed'];
    if (typeof embed === 'string') {
      const code = embedSource(node);
      const fallback = <div className="mp-embed mp-embed--loading">Rendering…</div>;
      const handleClick = onEmbedClick
        ? (e: ReactMouseEvent<HTMLDivElement>) => {
            // Only act on plain primary-button clicks — modifier-clicks are
            // reserved for future open-in-new / copy actions. Also ignore
            // when the click originated inside the Copy pill so the user
            // can use it without triggering the lightbox.
            if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            if ((e.target as HTMLElement)?.closest('.mp-block-copy')) return;
            const url = embedToDataUrl(e.currentTarget);
            if (!url) return;
            e.preventDefault();
            e.stopPropagation();
            onEmbedClick(url, embed);
          }
        : undefined;
      const wrapperClass = handleClick
        ? 'mp-embed-wrap mp-embed-wrap--clickable mp-block-copy-host'
        : 'mp-embed-wrap mp-block-copy-host';
      const block = blockRendererRegistry.get(embed)?.render(code, theme) ?? null;
      if (block) {
        // The embed's raw source text is the markdown content that
        // produced it — paste it into another editor and it round-trips.
        const getSource = (): string => code;
        return (
          <Suspense fallback={fallback}>
            <div
              className={wrapperClass}
              onClick={handleClick}
              role={handleClick ? 'button' : undefined}
              tabIndex={handleClick ? 0 : undefined}
              aria-label={
                handleClick ? `Open ${embed} preview in full screen` : undefined
              }
              onKeyDown={
                handleClick
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        const url = embedToDataUrl(e.currentTarget);
                        if (url) onEmbedClick?.(url, embed);
                      }
                    }
                  : undefined
              }
            >
              {block}
              <BlockCopyButton
                getSource={getSource}
                onCopyText={onCopyText}
                label="Copy source"
                copiedLabel="Copied"
                ariaLabel={`Copy ${embed} source`}
              />
            </div>
          </Suspense>
        );
      }
    }
    return <div {...rest}>{children}</div>;
  };
}

/** Read the resolved `--mp-font-prose` from the document root (sans/serif). */
function resolveProseFont(): string {
  if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') {
    return "'Inter Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
  }
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue('--mp-font-prose')
    .trim();
  return v || "'Inter Variable', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
}

/**
 * Map sanitized `<img>` elements so a relative `src` is rewritten via the
 * host's resolver (Tauri: asset-protocol URL for the doc's directory; web:
 * blob URL or passthrough). External / data / blob URLs are returned
 * unchanged by the resolver, so the rewrite is safe to apply unconditionally.
 * `node` is stripped so it never reaches the DOM.
 */
function makeImg(
  resolveSrc: (src: string) => string,
  onImageClick: ((src: string, alt: string) => void) | undefined,
): ComponentType<ImgProps> {
  return function PreviewImg({ node: _node, src, alt, ...rest }: ImgProps): ReactElement {
    const resolved = typeof src === 'string' && src ? resolveSrc(src) : src;
    const handleClick = onImageClick
      ? (e: React.MouseEvent<HTMLImageElement>) => {
          // Only act on plain primary-button clicks. Modifier-clicks fall
          // through (might be reserved for future open-in-new-window / copy).
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          if (typeof resolved !== 'string' || !resolved) return;
          e.preventDefault();
          e.stopPropagation();
          onImageClick(resolved, typeof alt === 'string' ? alt : '');
        }
      : undefined;
    return (
      <img
        {...rest}
        alt={alt}
        src={resolved}
        onClick={handleClick}
        style={{
          ...rest.style,
          cursor: handleClick ? 'zoom-in' : undefined,
        }}
      />
    );
  };
}

/**
 * Map sanitized `<a>` elements so clicks are intercepted: prevent the browser's
 * default navigation (which would try to load the href into the webview) and
 * hand the href to the host's `onLink` resolver instead. `node` is stripped so
 * it never reaches the DOM.
 */
function makeAnchor(onLink: (href: string) => void): ComponentType<AnchorProps> {
  return function PreviewAnchor({ node: _node, href, children, ...rest }: AnchorProps): ReactElement {
    return (
      <a
        {...rest}
        href={href}
        onClick={(e) => {
          if (typeof href !== 'string' || href === '') return;
          e.preventDefault();
          onLink(href);
        }}
      >
        {children}
      </a>
    );
  };
}

/**
 * Map fenced-code `<pre>` blocks so a hover-revealed "Copy" pill appears at
 * the top-right, matching the WYSIWYG (Crepe) code block's copy button
 * style and placement. The button reads the rendered `textContent` of the
 * underlying `<pre>` (which works regardless of Shiki's inner `<span>`
 * wrapping) and hands it to the host's clipboard writer.
 */
function makePre(onCopyText: (text: string) => void): ComponentType<PreProps> {
  return function PreviewPre({ node: _node, children, ...rest }: PreProps): ReactElement {
    const preRef = useRef<HTMLPreElement | null>(null);
    const getSource = useCallback(() => preRef.current?.textContent ?? '', []);
    return (
      <div className="mp-code-block mp-block-copy-host">
        <pre ref={preRef} {...rest}>
          {children}
        </pre>
        <BlockCopyButton
          getSource={getSource}
          onCopyText={onCopyText}
          label="Copy"
          copiedLabel="Copied"
          ariaLabel="Copy code"
        />
      </div>
    );
  };
}

/**
 * Wrap rendered `<table>` blocks in a positioning host with a hover-revealed
 * "Copy markdown" pill. Click yanks the original markdown source for the
 * table (sliced from the source via the `data-source-start`/`-end` offsets
 * stamped by `rehypeSourceLines`) and hands it to the host's clipboard
 * writer. No-ops if the offsets are missing — e.g. for HTML tables passed
 * through verbatim, where there is no markdown source to recover.
 */
function makeTable(
  getMarkdown: () => string,
  onCopyText: (text: string) => void,
): ComponentType<TableProps> {
  return function PreviewTable({ node, children, ...rest }: TableProps): ReactElement {
    const getSource = useCallback(
      () => sliceMarkdownForNode(node, getMarkdown()),
      [node],
    );
    return (
      <div className="mp-table-block mp-block-copy-host">
        <table {...rest}>{children}</table>
        <BlockCopyButton
          getSource={getSource}
          onCopyText={onCopyText}
          label="Copy markdown"
          copiedLabel="Copied"
          ariaLabel="Copy table as markdown"
        />
      </div>
    );
  };
}

type RehypeReactProcessor = Processor<undefined, undefined, undefined, HastRoot, ReactElement>;

/**
 * Read-only rendered view. Runs the trusted markdown pipeline in a Web Worker
 * (phase 2; see `pipelineClient`), KaTeX-hydrates on the main thread, then
 * **virtualizes** the top-level block list with `@tanstack/react-virtual`
 * (phase 4b): only the blocks in (and near) the viewport are mounted. Anchor
 * links and editor↔preview scroll-sync go through the imperative `Handle` —
 * they look up the target block by id/source-line in an in-memory map and
 * ask the virtualizer to scroll there, since the target may be unmounted.
 */
export const RenderedPreview = forwardRef<RenderedPreviewHandle, RenderedPreviewProps>(
  function RenderedPreview(
    {
      text,
      scrollRef,
      reading = false,
      readingFontScale,
      readingWidth,
      onLink,
      onCopyText,
      onResolveImageSrc,
      onImageClick,
      onEmbedClick,
    },
    handleRef,
  ): ReactElement {
    const dark = usePreviewTheme();
    const { serif, reader } = useThemeName();
    const { openContextMenu } = useContextMenu();
    // Debounce text changes so a burst of keystrokes triggers ONE render per
    // pause (spec §4.1) — without this every keystroke runs the full pipeline.
    const debouncedText = useDebouncedValue(text, RENDER_DEBOUNCE_MS);
    // Flat block array + lookup maps for slug/line → block index.
    const [flat, setFlat] = useState<FlattenedPreview | null>(null);
    // Last good render kept visible while recomputing; only swap on success.
    const lastGood = useRef<FlattenedPreview | null>(null);
    // Same-input render cache: when (debouncedText, dark, serif, reader)
    // match, reuse the prior FlattenedPreview — no pipeline run, no React
    // tree rebuild (spec §4.1 "skip re-render if AST hash unchanged").
    const renderCache = useRef<{
      text: string;
      dark: boolean;
      serif: boolean;
      reader: boolean;
      flat: FlattenedPreview;
    } | null>(null);
    // Reuse the rehype-react processor when theme params haven't changed.
    const processorRef = useRef<{ key: string; processor: RehypeReactProcessor } | null>(null);
    // Hold `onLink`/`onCopyText` in refs so the (async) render effect doesn't
    // re-run when the host passes a new function identity each render.
    const onLinkRef = useRef(onLink);
    onLinkRef.current = onLink;
    const onCopyTextRef = useRef(onCopyText);
    onCopyTextRef.current = onCopyText;
    // Ref-backed so the rehype-react processor stays cached across doc swaps —
    // the latest resolver is read at render time via the closure below.
    const onResolveImgRef = useRef(onResolveImageSrc);
    onResolveImgRef.current = onResolveImageSrc;
    const onImageClickRef = useRef(onImageClick);
    onImageClickRef.current = onImageClick;
    const onEmbedClickRef = useRef(onEmbedClick);
    onEmbedClickRef.current = onEmbedClick;
    // Tracks the latest source markdown so the cached rehype-react
    // processor's closures (Copy-as-markdown on tables, etc.) can pull
    // it without invalidating the processor cache when text changes.
    const textRef = useRef(text);
    textRef.current = text;

    // Build the preview context menu for a given target. If the target is
    // inside an <a>, offer Copy/Open link (Open link reuses the guarded onLink
    // handler — same allow-list as in App). Otherwise offer Copy of the
    // current selection.
    const buildMenu = useCallback(
      (target: Element | null): ContextMenuEntry[] => {
        const items: ContextMenuEntry[] = [];
        const selection = typeof window !== 'undefined' ? window.getSelection?.()?.toString() ?? '' : '';
        const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
        const href = anchor?.getAttribute('href') ?? '';

        items.push({
          id: 'copy',
          label: 'Copy',
          icon: Copy,
          shortcut: shortcutLabel('C'),
          disabled: selection.length === 0,
          onSelect: () => {
            if (selection) onCopyTextRef.current?.(selection);
          },
        });

        if (href) {
          items.push({ type: 'separator', id: 'sep' });
          items.push({
            id: 'copy-link',
            label: 'Copy link',
            icon: LinkIcon,
            onSelect: () => onCopyTextRef.current?.(href),
          });
          items.push({
            id: 'open-link',
            label: 'Open link',
            icon: ExternalLink,
            onSelect: () => onLinkRef.current?.(href),
          });
        }
        return items;
      },
      [],
    );

    const onContextMenu = useCallback(
      (e: ReactMouseEvent): void => {
        e.preventDefault();
        const items = buildMenu(e.target as Element);
        if (items.length > 0) openContextMenu(e.clientX, e.clientY, items);
      },
      [buildMenu, openContextMenu],
    );

    const longPress = useLongPress((x, y) => {
      const target = (document.elementFromPoint?.(x, y) ?? null) as Element | null;
      const items = buildMenu(target);
      if (items.length > 0) openContextMenu(x, y, items);
    });

    useEffect(() => {
      // Cache hit (same text + theme params): reuse the prior FlattenedPreview.
      const cached = renderCache.current;
      if (
        cached
        && cached.text === debouncedText
        && cached.dark === dark
        && cached.serif === serif
        && cached.reader === reader
      ) {
        if (lastGood.current !== cached.flat) {
          lastGood.current = cached.flat;
          startTransition(() => setFlat(cached.flat));
        }
        return;
      }

      let cancelled = false;
      void (async () => {
        try {
          mark('preview:render:start');
          const tree = await renderInWorker(debouncedText, { dark });
          if (cancelled) return;
          const diagramTheme: DiagramTheme = {
            dark,
            reader,
            fontFamily: resolveProseFont(),
          };
          const procKey = `${dark}|${serif}|${reader}|${diagramTheme.fontFamily}`;
          let cachedProc = processorRef.current;
          if (!cachedProc || cachedProc.key !== procKey) {
            cachedProc = {
              key: procKey,
              processor: unified().use(rehypeReact, {
                Fragment,
                jsx,
                jsxs,
                passNode: true,
                components: {
                  div: makeEmbedDiv(
                    diagramTheme,
                    (src, kind) => onEmbedClickRef.current?.(src, kind),
                    (text) => onCopyTextRef.current?.(text),
                  ),
                  a: makeAnchor((href) => onLinkRef.current?.(href)),
                  img: makeImg(
                    (src) => onResolveImgRef.current?.(src) ?? src,
                    (src, alt) => onImageClickRef.current?.(src, alt),
                  ),
                  pre: makePre((text) => onCopyTextRef.current?.(text)),
                  table: makeTable(
                    () => textRef.current,
                    (md) => onCopyTextRef.current?.(md),
                  ),
                },
              }) as RehypeReactProcessor,
            };
            processorRef.current = cachedProc;
          }
          const fullElement = cachedProc.processor.stringify(tree);
          const flattened = flattenBlocks(tree, fullElement);
          renderCache.current = { text: debouncedText, dark, serif, reader, flat: flattened };
          lastGood.current = flattened;
          // Mark the commit as a transition so React yields to user input
          // while reconciling a large block list (phase 3).
          startTransition(() => setFlat(flattened));
          measure('preview:render', 'preview:render:start');
        } catch (err) {
          if (cancelled) return;
          // Show the error only if we have nothing good to fall back to.
          if (!lastGood.current) {
            const errBlock: FlattenedPreview = {
              blocks: [
                {
                  index: 0,
                  key: 'error',
                  line: null,
                  ids: [],
                  element: <pre className="mp-error">{String(err)}</pre>,
                },
              ],
              maps: { bySlug: new Map(), byLine: new Map() },
            };
            setFlat(errBlock);
          }
        }
      })();
      return () => {
        cancelled = true;
      };
      // `serif` is in the deps so switching sans↔serif re-resolves the diagram
      // font; `reader` adjusts diagram palette softness.
    }, [debouncedText, dark, serif, reader]);

    // ─── virtualizer ───────────────────────────────────────────────────────
    const parentRef = useRef<HTMLDivElement | null>(null);
    const scrollRefRef = useRef(scrollRef);
    scrollRefRef.current = scrollRef;
    const setParentRef = useCallback((el: HTMLDivElement | null) => {
      parentRef.current = el;
      scrollRefRef.current?.(el);
    }, []);

    const blocks = flat?.blocks ?? [];
    const virtualizer = useVirtualizer({
      count: blocks.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => VIRTUAL_DEFAULT_HEIGHT,
      overscan: VIRTUAL_OVERSCAN,
      getItemKey: (i): string | number => blocks[i]?.key ?? i,
    });

    // ─── imperative handle (anchor + scroll-sync) ──────────────────────────
    useImperativeHandle(
      handleRef,
      (): RenderedPreviewHandle => ({
        scrollEl: () => parentRef.current,
        scrollToSlug: (slug) => {
          const idx = flat?.maps.bySlug.get(slug);
          if (idx === undefined) return false;
          virtualizer.scrollToIndex(idx, { align: 'start' });
          return true;
        },
        scrollToLine: (line) => {
          const map = flat?.maps.byLine;
          if (!map || map.size === 0) return false;
          // Greatest mapped line ≤ requested. Maps are insertion-ordered by
          // ascending line (we built byLine in pipeline-emission order), so
          // a single linear pass is fine. For O(log n) we could keep a sorted
          // keys array — defer unless this shows up in profiles.
          let bestLine = -1;
          let bestIdx = -1;
          for (const [k, v] of map) {
            if (k <= line && k > bestLine) {
              bestLine = k;
              bestIdx = v;
            }
          }
          if (bestIdx < 0) return false;
          virtualizer.scrollToIndex(bestIdx, { align: 'start' });
          return true;
        },
        topLine: () => {
          const items = virtualizer.getVirtualItems();
          if (!items.length) return null;
          const scrollTop = parentRef.current?.scrollTop ?? 0;
          for (const it of items) {
            if (it.end > scrollTop) return blocks[it.index]?.line ?? null;
          }
          return null;
        },
        topSlug: () => {
          const items = virtualizer.getVirtualItems();
          if (!items.length) return null;
          const scrollTop = parentRef.current?.scrollTop ?? 0;
          for (const it of items) {
            if (it.end > scrollTop) return blocks[it.index]?.ids[0] ?? null;
          }
          return null;
        },
      }),
      [flat, virtualizer, blocks],
    );

    const className = reading ? 'mp-preview mp-preview--reading' : 'mp-preview';
    const items = virtualizer.getVirtualItems();
    const totalSize = virtualizer.getTotalSize();
    // Reading-only style + data attributes. Skipped in split mode so the
    // preview keeps 1:1 Crepe parity (no zoom / no width preset).
    const rootStyle: React.CSSProperties | undefined =
      reading && typeof readingFontScale === 'number'
        ? ({ ['--mp-reading-font-scale' as string]: String(readingFontScale) } as React.CSSProperties)
        : undefined;
    const rootWidthAttr = reading && readingWidth ? readingWidth : undefined;

    return (
      <div
        className={className}
        data-testid="rendered-preview"
        data-reading-width={rootWidthAttr}
        ref={setParentRef}
        style={rootStyle}
        onContextMenu={onContextMenu}
        onTouchStart={longPress.onTouchStart}
        onTouchMove={longPress.onTouchMove}
        onTouchEnd={longPress.onTouchEnd}
        onTouchCancel={longPress.onTouchCancel}
      >
        <div
          className="mp-preview__body"
          style={{ position: 'relative', height: blocks.length ? totalSize : undefined }}
        >
          {items.map((vi) => {
            const block = blocks[vi.index];
            if (!block) return null;
            return (
              <div
                key={block.key}
                ref={virtualizer.measureElement}
                className="mp-block"
                data-index={vi.index}
                data-block-index={vi.index}
                data-line={block.line ?? undefined}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${vi.start}px)`,
                }}
              >
                {block.element as ReactNode}
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);

RenderedPreview.displayName = 'RenderedPreview';
